import { useCallback } from "react";
import usePage from "../../components/layouts/base-page-layout/page-hook";
import dashboardSettingsStore from "../../stores/dashboard-settings-store";

/**
 * this method will call sizeChangeCallback while element's size is changing
 * and will stop listening to size changes after 20ms of no changes
 * */
function listenToSizeChanges(element: Element, sizeChangeCallback: () => void): void {
    let _timeoutDisconnect: number | undefined = undefined;
    const observer = new ResizeObserver((entries) => {
        sizeChangeCallback();
        window.clearTimeout(_timeoutDisconnect);
        _timeoutDisconnect = window.setTimeout(() => {
            sizeChangeCallback();
            observer.disconnect();
        }, 50);
    });
    sizeChangeCallback();
    observer.observe(element as Element);
}

function getScrollDownDeltaY(element: Element, topOffset: number, bottomOffset: number) {
    let scrollDeltaY = 0;
    const rects = element.getBoundingClientRect();
    /** number of px the element is under screen's bottom edge, if negative -> inside the screen */
    const bottomDelta = rects.bottom - window.innerHeight + bottomOffset;
    /** number of px the element is above the top screens offset, if negative -> outside the screen */
    const upperDelta = rects.top - topOffset;

    /** when upper part is lower than the maximum top position it means we have a place to scroll
     * and when the lower part is under the screen it means we need to scroll  */
    if (upperDelta > 0 && bottomDelta > 0) {
        scrollDeltaY = Math.min(upperDelta, bottomDelta);
    }
    return scrollDeltaY;
}

const getScrollToFn = (containerRef: React.RefObject<HTMLDivElement>) => {
    if (dashboardSettingsStore.showHeaderAndSidebar) {
        return (y: number) => {
            const scrollY = containerRef.current?.scrollTop ?? 0;
            containerRef.current?.scrollTo({
                top: scrollY + y,
                behavior: 'smooth',
            });
        }
    }

    return (y: number) => {
        window.scrollTo({
            top: window.scrollY + y,
            behavior: 'smooth',
        });
    }
}

const useScroll = () => {
    const { getPageTopOffset, containerRef } = usePage();

    const scrollIntoView = useCallback((element: Element) => {
        if (!element || !getPageTopOffset) {
            return;
        }

        const scrollTo = getScrollToFn(containerRef);

        listenToSizeChanges(element, () => {
            const scrollDeltaY = getScrollDownDeltaY(element, getPageTopOffset() + 10, 20);

            if (scrollDeltaY > 0) {
                scrollTo(scrollDeltaY);
            }
        });

    }, [getPageTopOffset]);

    return {
        scrollIntoView
    }
}


export default useScroll;
